import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Radio, Users, CheckCircle2, UserPlus, RefreshCw,
  ExternalLink, ArrowUpRight, Clock, Volume2, VolumeX,
  Send, Star, AlertTriangle, Loader2,
} from "lucide-react";
import { orgApi } from "../lib/organizerApi";

/**
 * Phase 8.9 — Live "pit-wall" dashboard tile for the organizer.
 *
 * Renders a full-width card at the top of `/organizer/dashboard` whenever the
 * organizer has an active, non-archived event. Auto-refreshes stats every 30s,
 * shows a scrolling ticker of the last 5 check-ins, and highlights walk-ins vs
 * pre-registrations. Zero user interaction is required to stay useful — leave
 * the tab open all night.
 *
 * Phase 8.9.1 (Aug 11 event prep) — ambient event pulse: when a new check-in
 * arrives between refreshes, plays a soft two-note chime (Web Audio, no asset)
 * and briefly flashes the counter green. Sound preference persists in
 * localStorage, defaults ON.
 */
const REFRESH_MS = 30_000;
const SOUND_PREF_KEY = "nailViet.liveTile.sound";

export default function LiveEventTile({ event }) {
  const [stats, setStats] = useState({
    total: event?.stats?.total ?? 0,
    checked: event?.stats?.checked ?? 0,
    walkins: event?.stats?.walkins ?? 0,
    recent_checkins: [],
    late_arrivals: [],
    late_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [countdown, setCountdown] = useState(REFRESH_MS / 1000);
  const [soundOn, setSoundOn] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage?.getItem(SOUND_PREF_KEY);
    return v === null ? true : v === "1";
  });
  const [flashKey, setFlashKey] = useState(0); // increments on every new check-in
  const timerRef = useRef(null);
  const countRef = useRef(null);
  const seenIdsRef = useRef(new Set()); // registration IDs seen in previous fetches
  const audioCtxRef = useRef(null);
  const isFirstLoadRef = useRef(true);

  const persistSoundPref = (next) => {
    setSoundOn(next);
    try { window.localStorage?.setItem(SOUND_PREF_KEY, next ? "1" : "0"); } catch (_e) { /* ignore */ }
  };

  // Web Audio soft two-note chime (A5 → D6, gentle envelope). No external asset.
  // Phase 8.16 — when `vip=true`, plays a richer three-note ascend (A5 → D6 → F#6)
  // so organizers hear a distinct "VIP arrived" flourish across the venue.
  const playChime = (vip = false) => {
    if (!soundOn) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const notes = vip
        ? [
            { freq: 880.0,   start: 0.00, dur: 0.20, gain: 0.20 }, // A5
            { freq: 1174.66, start: 0.13, dur: 0.24, gain: 0.22 }, // D6
            { freq: 1479.98, start: 0.28, dur: 0.42, gain: 0.24 }, // F#6 — VIP flourish
          ]
        : [
            { freq: 880.0,   start: 0.00, dur: 0.22, gain: 0.18 }, // A5
            { freq: 1174.66, start: 0.14, dur: 0.32, gain: 0.18 }, // D6
          ];
      notes.forEach(({ freq, start, dur, gain: g }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + start);
        gain.gain.linearRampToValueAtTime(g, now + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + start);
        osc.stop(now + start + dur + 0.02);
      });
    } catch (_e) { /* ignore audio failures */ }
  };

  const load = async () => {
    if (!event?.slug) return;
    setLoading(true);
    try {
      const { data } = await orgApi.get(`/organizer/events/${event.slug}/stats`);
      const recent = data.recent_checkins || [];

      // Detect newly-arrived check-ins by comparing IDs to the last fetch.
      // On first load we PRIME the seen set (no chime for existing check-ins).
      const currentIds = new Set(recent.map((r) => r.id));
      if (isFirstLoadRef.current) {
        seenIdsRef.current = currentIds;
        isFirstLoadRef.current = false;
      } else {
        const newOnes = recent.filter((r) => !seenIdsRef.current.has(r.id));
        if (newOnes.length > 0) {
          // Phase 8.16 — VIP flourish if any newly-arrived row is a VIP.
          const anyVip = newOnes.some((r) => r.is_vip);
          playChime(anyVip);
          setFlashKey((k) => k + 1);
        }
        seenIdsRef.current = currentIds;
      }

      setStats({
        total: data.total ?? 0,
        checked: data.checked ?? 0,
        walkins: data.walkins ?? 0,
        recent_checkins: recent,
        late_arrivals: data.late_arrivals || [],
        late_count: data.late_count || 0,
      });
      setLastUpdatedAt(new Date());
    } catch (_e) {
      // silent: keep the last-good numbers
    } finally {
      setLoading(false);
      setCountdown(REFRESH_MS / 1000);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    countRef.current = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => {
      clearInterval(timerRef.current);
      clearInterval(countRef.current);
    };
  }, [event?.slug]);

  const total = stats.total;
  const checked = stats.checked;
  const walkins = stats.walkins;
  const preReg = Math.max(0, total - walkins);
  const percent = total > 0 ? Math.round((checked / total) * 100) : 0;
  const remaining = Math.max(0, total - checked);

  return (
    <section
      data-testid="live-event-tile"
      className="mb-8 rounded-3xl overflow-hidden border border-emerald-900/20 bg-gradient-to-br from-[#0F1A2E] via-[#122845] to-[#0F1A2E] text-cream shadow-xl relative"
    >
      {/* Radial accent */}
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-amber-300/10 blur-3xl pointer-events-none" />

      <div className="relative p-5 sm:p-6">
        {/* Header — LIVE badge + event name + refresh */}
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold text-emerald-300 mb-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <Radio className="w-3 h-3" /> Đang diễn ra · Live
            </div>
            <h2
              data-testid="live-event-name"
              className="text-xl sm:text-2xl font-serif leading-tight truncate"
            >
              {event.name}
            </h2>
            <p className="text-xs text-cream/60 mt-1 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> {event.event_date} · {event.location}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => persistSoundPref(!soundOn)}
              data-testid="live-sound-toggle"
              aria-label={soundOn ? "Tắt âm thanh check-in" : "Bật âm thanh check-in"}
              aria-pressed={soundOn}
              className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold transition ${
                soundOn
                  ? "border-emerald-300/40 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/15"
                  : "border-cream/20 bg-white/5 text-cream/60 hover:bg-white/10"
              }`}
            >
              {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{soundOn ? "Âm thanh: BẬT" : "Âm thanh: TẮT"}</span>
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="live-refresh"
              className="inline-flex items-center gap-1.5 border border-cream/20 bg-white/5 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-white/10 disabled:opacity-50 transition"
              aria-label="Làm mới ngay"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Đang tải…" : `Làm mới (${countdown}s)`}
            </button>
          </div>
        </div>

        {/* Big check-in progress */}
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-5 items-stretch">
          <div
            key={flashKey}
            data-testid="live-counter-panel"
            data-flash={flashKey > 0 ? "1" : "0"}
            className={`rounded-2xl bg-black/25 border p-5 transition-all duration-700 ${
              flashKey > 0 ? "border-emerald-300/70 shadow-[0_0_40px_rgba(52,211,153,0.35)] live-tile-flash" : "border-cream/10"
            }`}
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/60 font-semibold mb-2">
              Đã check-in tối nay
            </div>
            <div className="flex items-baseline gap-3">
              <div
                data-testid="live-checked-count"
                className="text-6xl sm:text-7xl font-serif font-semibold text-emerald-300 tabular-nums leading-none"
              >
                {checked}
              </div>
              <div className="text-cream/50 font-sans text-lg">
                / <span data-testid="live-total-count">{total}</span> khách
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between items-baseline mb-1.5">
                <span className="text-[11px] text-cream/60 uppercase tracking-[0.18em]">Tiến độ</span>
                <span
                  data-testid="live-percent"
                  className="text-xs font-semibold text-emerald-300 tabular-nums"
                >
                  {percent}%
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  data-testid="live-progress-bar"
                  className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-emerald-300 to-amber-300 transition-[width] duration-700 ease-out"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className="text-[11px] text-cream/50 mt-2">
                Còn <span className="font-semibold text-cream/80" data-testid="live-remaining">{remaining}</span> khách chưa đến
              </div>
            </div>
          </div>

          {/* Breakdown pills */}
          <div className="grid grid-cols-2 gap-3">
            <BreakdownTile
              icon={<Users className="w-4 h-4" />}
              label="Đăng ký trước"
              value={preReg}
              tone="sky"
              testId="live-prereg"
            />
            <BreakdownTile
              icon={<UserPlus className="w-4 h-4" />}
              label="Walk-in"
              value={walkins}
              tone="amber"
              testId="live-walkins"
            />
            <BreakdownTile
              icon={<CheckCircle2 className="w-4 h-4" />}
              label="Đã check-in"
              value={checked}
              tone="emerald"
              testId="live-checked-pill"
            />
            <BreakdownTile
              icon={<Clock className="w-4 h-4" />}
              label="Còn lại"
              value={remaining}
              tone="rose"
              testId="live-remaining-pill"
            />
          </div>
        </div>

        {/* Recent check-ins ticker */}
        <div className="mt-5 rounded-2xl bg-black/25 border border-cream/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/60 font-semibold">
              Check-in gần nhất
            </div>
            <div className="text-[10px] text-cream/40">
              {lastUpdatedAt ? `Cập nhật ${formatAgo(lastUpdatedAt)}` : "Đang chờ…"}
            </div>
          </div>
          {stats.recent_checkins.length === 0 ? (
            <div
              data-testid="live-recent-empty"
              className="py-5 text-center text-cream/40 text-sm"
            >
              Chưa có khách nào check-in. Sẵn sàng đón khách đầu tiên.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="live-recent-list">
              {stats.recent_checkins.map((r) => (
                <li
                  key={r.id}
                  data-testid={`live-recent-${r.id}`}
                  className={`flex items-center justify-between gap-3 rounded-xl transition px-3 py-2.5 ${
                    r.is_vip
                      ? "bg-gradient-to-r from-amber-300/15 via-amber-200/5 to-transparent border border-amber-300/40 shadow-[0_0_18px_-6px_rgba(251,191,36,0.35)]"
                      : "bg-white/5 hover:bg-white/[0.08]"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-cream truncate flex items-center gap-2">
                      {r.is_vip ? (
                        <span
                          data-testid={`live-recent-vip-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-amber-300/25 text-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] ring-1 ring-amber-200/40"
                          title="VIP"
                        >
                          <Star className="w-2.5 h-2.5 fill-amber-200" />
                          VIP
                        </span>
                      ) : null}
                      <span className="truncate">{r.business_name || r.owner_name || "Khách"}</span>
                    </div>
                    <div className="text-[11px] text-cream/50 truncate">
                      {r.owner_name && r.business_name ? r.owner_name : ""}
                      {r.is_walkin ? (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-300/15 text-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                          Walk-in
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="text-[11px] text-cream/50 whitespace-nowrap tabular-nums">
                    {formatAgo(r.checked_in_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Phase 8.13.C — Late Arrivals list (pre-reg still not checked in) */}
        {stats.late_count > 0 && (
          <div className="mt-4 rounded-2xl bg-amber-500/10 border border-amber-400/40 p-4">
            <div className="flex items-center justify-between mb-3 gap-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-200 font-semibold inline-flex items-center gap-1.5">
                <span className="inline-flex w-1.5 h-1.5 rounded-full bg-amber-300" />
                {stats.late_count === 1
                  ? "1 khách chưa tới"
                  : `${stats.late_count} khách chưa tới`}
              </div>
              <SweepButton slug={event.slug} onDone={load} />
            </div>
            <ul className="space-y-2" data-testid="late-arrivals-list">
              {stats.late_arrivals.slice(0, 5).map((r) => (
                <LateArrivalRow
                  key={r.id}
                  reg={r}
                  slug={event.slug}
                  onSent={load}
                />
              ))}
            </ul>
          </div>
        )}

        {/* Footer actions */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Link
            to={`/organizer/events/${event.slug}/registrations`}
            data-testid="live-open-regs"
            className="inline-flex items-center gap-1.5 bg-cream text-espresso rounded-full px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-white transition"
          >
            <Users className="w-3.5 h-3.5" /> Danh sách đăng ký
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <a
            href={`/events/${event.slug}/checkin-kiosk`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="live-open-kiosk"
            className="inline-flex items-center gap-1.5 border border-cream/25 text-cream rounded-full px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-white/10 transition"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Mở màn hình kiosk
          </a>
          {/* Phase 8.16 — Wall Board projection */}
          <a
            href={`/organizer/events/${event.slug}/wall`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="live-open-wall-board"
            className="inline-flex items-center gap-1.5 border border-amber-300/40 text-amber-100 rounded-full px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-amber-300/10 transition"
          >
            <Radio className="w-3.5 h-3.5" /> Chiếu TV
          </a>
        </div>
      </div>
    </section>
  );
}

function BreakdownTile({ icon, label, value, tone, testId }) {
  const tones = {
    emerald: "text-emerald-300 border-emerald-300/30",
    amber:   "text-amber-200 border-amber-300/30",
    sky:     "text-sky-300 border-sky-300/30",
    rose:    "text-rose-300 border-rose-300/30",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl bg-black/25 border ${tones[tone] || "border-cream/15"} p-3.5 flex flex-col justify-between`}
    >
      <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${tones[tone]?.split(" ")[0] || "text-cream"}`}>
        {icon} {label}
      </div>
      <div className="text-3xl font-serif tabular-nums text-cream leading-none mt-2">{value}</div>
    </div>
  );
}

function LateArrivalRow({ reg, slug, onSent }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // "sent" | "skip" | "fail"
  const canSend = reg.is_verified && status !== "sent";
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await orgApi.post(`/organizer/events/${slug}/late-arrivals/${reg.id}/nudge`);
      if (data.sent) {
        setStatus("sent");
        setTimeout(() => onSent?.(), 800);
      } else {
        setStatus(data.reason === "unverified_skip" ? "skip" : "fail");
      }
    } catch (_e) {
      setStatus("fail");
    } finally {
      setBusy(false);
    }
  };
  return (
    <li
      data-testid={`late-arrival-${reg.id}`}
      className="flex items-center justify-between gap-3 rounded-xl bg-black/25 hover:bg-black/35 transition px-3 py-2.5"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {reg.is_vip && <Star className="w-3 h-3 text-gold-300 fill-gold-400 flex-shrink-0" />}
          <span className="text-sm font-semibold text-cream truncate">
            {reg.business_name || reg.owner_name || "Khách"}
          </span>
        </div>
        <div className="text-[11px] text-cream/50 truncate mt-0.5 flex items-center gap-1.5">
          <span className="tabular-nums">{reg.phone_normalized}</span>
          {!reg.is_verified && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/25 text-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase">
              <AlertTriangle className="w-2 h-2" /> Chưa XM
            </span>
          )}
        </div>
      </div>
      {status === "sent" ? (
        <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-semibold">
          <CheckCircle2 className="w-3.5 h-3.5" /> Đã gửi
        </span>
      ) : status === "skip" ? (
        <span className="text-xs text-amber-200/80">Bỏ qua</span>
      ) : (
        <button
          type="button"
          onClick={send}
          disabled={!canSend || busy}
          data-testid={`late-nudge-${reg.id}`}
          title={reg.is_verified ? "Gửi SMS: bạn tới đâu rồi?" : "Số chưa xác minh — không gửi được"}
          className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-semibold whitespace-nowrap transition ${
            canSend
              ? "bg-amber-300 text-espresso hover:bg-amber-200"
              : "bg-white/10 text-cream/40 cursor-not-allowed"
          }`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          Nhắn
        </button>
      )}
    </li>
  );
}

function SweepButton({ slug, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { sent, skipped, failed, eligible }

  const clickSweep = async () => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    try {
      // Step 1: preview how many verified late arrivals would be nudged.
      const preview = await orgApi.get(`/organizer/events/${slug}/late-arrivals/sweep/preview`);
      const eligible = preview.data.eligible_count ?? 0;
      const alreadyNudged = preview.data.already_nudged_count ?? 0;
      if (eligible === 0) {
        const msg = alreadyNudged > 0
          ? `Không còn ai cần nhắn. Đã nhắn ${alreadyNudged} khách trước đó.`
          : "Không có khách verified nào chưa tới.";
        setResult({ eligible: 0, sent: 0, skipped: 0, failed: 0, msg });
        return;
      }
      // Step 2: confirm dialog with exact count.
      const ok = window.confirm(
        `Sweep now: gửi SMS "sự kiện đang bắt đầu" tới ${eligible} khách verified?\n\n` +
        `Đã nhắn trước đó: ${alreadyNudged} khách (sẽ tự động bỏ qua).\n` +
        `Số chưa xác minh sẽ KHÔNG nhận SMS.`
      );
      if (!ok) return;
      // Step 3: fire the sweep.
      const { data } = await orgApi.post(`/organizer/events/${slug}/late-arrivals/sweep`);
      setResult(data);
      setTimeout(() => { onDone?.(); }, 1000);
    } catch (_e) {
      setResult({ eligible: 0, sent: 0, skipped: 0, failed: 0, msg: "Lỗi khi gửi Sweep" });
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const total = (result.sent || 0) + (result.skipped || 0);
    return (
      <div
        data-testid="sweep-result"
        className="text-[10px] text-amber-100 bg-amber-500/15 border border-amber-300/30 rounded-full px-3 py-1.5 font-semibold"
      >
        {result.msg
          ? result.msg
          : `Đã xử lý ${total}/${result.eligible} khách (thành công ${result.sent}, dry-run ${result.skipped}, lỗi ${result.failed})`}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={clickSweep}
      disabled={busy}
      data-testid="sweep-now-button"
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] transition ${
        busy
          ? "bg-white/10 text-cream/40 cursor-not-allowed"
          : "bg-amber-300 text-espresso hover:bg-amber-200 active:scale-[0.97]"
      }`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
      Sweep now
    </button>
  );
}

function formatAgo(input) {
  if (!input) return "";
  const then = input instanceof Date ? input : new Date(input);
  const diffMs = Date.now() - then.getTime();
  const s = Math.max(0, Math.floor(diffMs / 1000));
  if (s < 30) return "vừa xong";
  if (s < 60) return `${s}s trước`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} phút trước`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} giờ trước`;
  const d = Math.floor(h / 24);
  return `${d} ngày trước`;
}
